import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getProfile: () => api.get('/auth/me'),
  getWarehouseAddress: () => api.get('/auth/warehouse-address'),
};

// Packages (Customer)
export const packagesAPI = {
  getMyPackages: () => api.get('/packages'),
  getPackage: (id) => api.get(`/packages/${id}`),
  saveCustomsDeclaration: (id, data) => api.post(`/packages/${id}/customs-declaration`, data),
  getCustomsDeclaration: (id) => api.get(`/packages/${id}/customs-declaration`),
};

// Admin Packages
export const adminPackagesAPI = {
  getAll: (params) => api.get('/admin/packages', { params }),
  create: (data) => api.post('/admin/packages', data),
  update: (id, data) => api.put(`/admin/packages/${id}`, data),
  delete: (id) => api.delete(`/admin/packages/${id}`),
};

// Admin Customers
export const adminCustomersAPI = {
  getAll: () => api.get('/admin/customers'),
  getById: (id) => api.get(`/admin/customers/${id}`),
  searchByNumber: (number) => api.get(`/admin/customers/search/${number}`),
  delete: (id) => api.delete(`/admin/customers/${id}`),
};

// Shipping Rates
export const shippingRatesAPI = {
  getAll: () => api.get('/shipping-rates'),
  create: (data) => api.post('/admin/shipping-rates', data),
  update: (id, data) => api.put(`/admin/shipping-rates/${id}`, data),
  delete: (id) => api.delete(`/admin/shipping-rates/${id}`),
};

// Payments
export const paymentsAPI = {
  createCheckout: (packageId, originUrl, shippingOptionId = null) => api.post('/payments/checkout', { 
    package_id: packageId, 
    origin_url: originUrl,
    shipping_option_id: shippingOptionId
  }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Tracking (Public)
export const trackingAPI = {
  track: (trackingNumber) => api.get(`/tracking/${trackingNumber}`),
};

// Admin Stats
export const adminStatsAPI = {
  getStats: () => api.get('/admin/stats'),
  seedRates: () => api.post('/admin/seed-rates'),
};

// Setup
export const setupAPI = {
  createAdmin: () => api.post('/setup/admin'),
};

// Shipping Options Calculator
export const shippingCalcAPI = {
  calculateOptions: (weight, destination) => api.get('/shipping/calculate-options', { params: { weight, destination } }),
};

// Pro Quote
export const proAPI = {
  submitQuote: (data) => api.post('/pro/quote', data),
};

// ISM Deals - Public
export const dealsAPI = {
  meta: () => api.get('/deals-meta/destinations'),
  list: () => api.get('/deals'),
  showcase: (limit = 3) => api.get('/deals-showcase', { params: { limit } }),
  getBySlug: (slug) => api.get(`/deals/${slug}`),
  getProduct: (slug, productSlug) => api.get(`/deals/${slug}/products/${productSlug}`),
  expressInterest: (dealId, dpId, data) => api.post(`/deals/${dealId}/products/${dpId}/interest`, data),
  declareOrder: (dealId, dpId, data) => api.post(`/deals/${dealId}/products/${dpId}/declare-order`, data),
  trackClick: (dealId, dpId) => api.post(`/deals/${dealId}/products/${dpId}/click-merchant`),
  joinWaitlist: (data) => api.post('/deals/waitlist', data),
  checkout: (dealId, dpId, originUrl) => api.post(`/deals/${dealId}/products/${dpId}/checkout`, { origin_url: originUrl }),
  myDeals: () => api.get('/my/deals'),
  paymentStatus: (orderId) => api.get(`/my/deals/${orderId}/payment-status`),
};

// ISM Deals - Admin
export const adminDealsAPI = {
  // Categories
  listCategories: () => api.get('/admin/deals/categories'),
  createCategory: (data) => api.post('/admin/deals/categories', data),
  updateCategory: (id, data) => api.put(`/admin/deals/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/deals/categories/${id}`),
  // Products
  listProducts: () => api.get('/admin/deals/products'),
  createProduct: (data) => api.post('/admin/deals/products', data),
  updateProduct: (id, data) => api.put(`/admin/deals/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/admin/deals/products/${id}`),
  duplicateProduct: (id) => api.post(`/admin/deals/products/${id}/duplicate`),
  fetchFromUrl: (url) => api.post('/admin/deals/products/fetch-from-url', { url }),
  uploadPhoto: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/deals/products/upload-photo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Deals
  listDeals: () => api.get('/admin/deals'),
  createDeal: (data) => api.post('/admin/deals', data),
  updateDeal: (id, data) => api.put(`/admin/deals/${id}`, data),
  deleteDeal: (id) => api.delete(`/admin/deals/${id}`),
  duplicateDeal: (id) => api.post(`/admin/deals/${id}/duplicate`),
  // Deal products
  listDealProducts: (dealId) => api.get(`/admin/deals/${dealId}/products`),
  attachProduct: (dealId, data) => api.post(`/admin/deals/${dealId}/products`, data),
  updateDealProduct: (dealId, dpId, data) => api.put(`/admin/deals/${dealId}/products/${dpId}`, data),
  detachProduct: (dealId, dpId) => api.delete(`/admin/deals/${dealId}/products/${dpId}`),
  // Orders
  listOrders: (params = {}) => api.get('/admin/deals/orders', { params }),
  receiveOrder: (id, data) => api.post(`/admin/deals/orders/${id}/receive`, data),
  updateOrderStatus: (id, data) => api.put(`/admin/deals/orders/${id}/status`, data),
  // Pallets
  listPallets: (dealId = null) => api.get('/admin/deals/pallets', { params: dealId ? { deal_id: dealId } : {} }),
  createPallet: (data) => api.post('/admin/deals/pallets', data),
  updatePallet: (id, data) => api.put(`/admin/deals/pallets/${id}`, data),
  deletePallet: (id) => api.delete(`/admin/deals/pallets/${id}`),
  assignOrder: (palletId, orderId) => api.post(`/admin/deals/pallets/${palletId}/assign/${orderId}`),
  unassignOrder: (palletId, orderId) => api.post(`/admin/deals/pallets/${palletId}/unassign/${orderId}`),
  palletItems: (palletId) => api.get(`/admin/deals/pallets/${palletId}/items`),
  // Waitlist
  listWaitlist: () => api.get('/admin/deals/waitlist'),
  // Stats dashboard
  stats: () => api.get('/admin/deals/stats'),
  // Cost calculator
  costCalculator: (data) => api.post('/admin/deals/cost-calculator', data),
  // Profitability simulator
  profitability: (dealId) => api.get(`/admin/deals/${dealId}/profitability`),
  // Publishing shortcuts
  publishDeal: (id) => api.post(`/admin/deals/${id}/publish`),
  unpublishDeal: (id) => api.post(`/admin/deals/${id}/unpublish`),
  previewDeal: (id) => api.get(`/admin/deals/${id}/preview`),
  // Seed
  seedSample: () => api.post('/admin/deals/seed-sample'),
};

export default api;
export { api };

export const analyticsAPI = {
  summary: () => api.get('/admin/analytics/summary'),
};

// Track a named event (button click, funnel step). Fire-and-forget.
export const trackEvent = (name, data = {}) => {
  try {
    let sid = localStorage.getItem('ism_sid');
    if (!sid) {
      sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('ism_sid', sid);
    }
    api.post('/track', {
      path: window.location.pathname,
      referrer: document.referrer || '',
      session_id: sid,
      event_name: String(name).slice(0, 64),
      event_data: data || {},
    }).catch(() => {});
  } catch { /* no-op */ }
};
